import axiosInstance from "../utils/axiosConfig.ts";

export type SmartOLTOverview = {
  total_olts: number;
  total_onus: number;
  online_onus: number;
  offline_onus: number;
  unconfigured_onus?: number;
  low_signal_onus?: number;
  warning_onus?: number;
  critical_onus?: number;
  tenant_configured?: boolean;
  last_onu_sync?: string | null;
};

export type SmartOLTHealth = {
  ok?: boolean;
  error?: { code?: string; message?: string };
};

export type SmartOLTTenantConfig = {
  configured: boolean;
  enabled: boolean;
  id?: string;
  company_id?: string;
  base_url?: string | null;
  subdomain?: string | null;
  budget_per_hour?: number;
  feature_flags?: Record<string, unknown>;
  updated_at?: string | null;
};

export type SmartOLTTenantConfigUpdate = {
  enabled?: boolean;
  subdomain?: string | null;
  base_url?: string | null;
  budget_per_hour?: number;
  api_key?: string;
};

export type SmartOLTOltRow = {
  id: string;
  olt_id: string;
  name: string;
  hardware_version?: string | null;
  ip?: string | null;
  telnet_port?: string | null;
  snmp_port?: string | null;
  uptime?: string | null;
  env_temp?: string | null;
  fetched_at?: string | null;
};

export type SmartOLTStatusKind = "online" | "offline" | "los" | "power";

export type SmartOLTOnuRow = {
  id: string;
  onu_external_id: string;
  olt_id?: string | null;
  olt_name?: string | null;
  board?: string | null;
  port?: string | null;
  onu_number?: string | null;
  sn?: string | null;
  mac?: string | null;
  status?: string | null;
  status_kind?: SmartOLTStatusKind | null;
  admin_status?: string | null;
  catv_status?: string | null;
  signal_rx?: number | null;
  signal_tx?: number | null;
  // GAP-08: GPS and location fields
  latitude?: number | null;
  longitude?: number | null;
  zone_name?: string | null;
  odb_name?: string | null;
  address?: string | null;
  customer_id?: string | null;
  customer_internet_id?: string | null;
  customer_name?: string | null;
  smartolt_name?: string | null;
  smartolt_custom_id?: string | null;
  is_in_db?: boolean;
  onu_type_name?: string | null;
  onu_type_id?: string | null;
  pon_type?: string | null;
  vlan?: string | number | null;
  mgmt_ip?: string | null;
  mgmt_subnet?: string | null;
  mgmt_gateway?: string | null;
  mgmt_dns1?: string | null;
  mgmt_dns2?: string | null;
  mgmt_vlan?: string | number | null;
  wan_mode?: string | null;
  voip?: string | number | boolean | null;
  tr069?: string | null;
  auth_date?: string | null;
  pon_port?: string | null;
  distance?: number | string | null;
  last_online_at?: string | null;
  offline_days?: number | null;
  cause?: string | null;
  fetched_at?: string | null;
};

export type SmartOLTOnuWebAdminLink = {
  url: string;
  mgmt_ip: string;
  expires_at: number;
};

/** GAP-02: An ONU connected physically but not yet provisioned in SmartOLT */
export type SmartOLTUnconfiguredOnuRow = {
  olt_id: string;
  olt_name?: string | null;
  board: string;
  port: string;
  onu_number: string;
  sn: string | null;
  pon_type: string | null;
  onu_type_name?: string | null;
  onu_type_id?: string | null;
  pon_description?: string | null;
  is_disabled?: number | boolean | null;
  actions?: string[];
  fetched_at: string | null;
};

export type SmartOLTCommandRow = {
  id: string;
  action_key: string;
  display_name?: string | null;
  target_type: string;
  target_id?: string | null;
  status: string;
  retry_count: number;
  max_retries: number;
  error_code?: string | null;
  error_message?: string | null;
  created_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  targets?: Array<{ target_id: string; status: string; error?: string | null }>;
};

export type SmartOLTAlarmRow = {
  id: string;
  time?: string | null;
  severity: string;
  device: string;
  olt_id?: string | null;
  olt_name?: string | null;
  onu_external_id?: string | null;
  onu_name?: string | null;
  message: string;
  oid?: string | null;
  content?: string | null;
  raw?: string | null;
  state: "open" | "acked" | "cleared" | string;
  ack_at?: string | null;
  ack_by?: string | null;
  clear_at?: string | null;
  clear_by?: string | null;
  source?: string | null;
};

export type SmartOLTAlarmList = {
  alarms: SmartOLTAlarmRow[];
  counts: { critical: number; warning: number; other: number; open: number; total: number };
  filtered_count: number;
};

export type SmartOLTCapacityPort = {
  olt_id: string;
  olt_name?: string | null;
  board?: string | null;
  port?: string | null;
  pon_name?: string | null;
  pon_type?: string | null;
  onus: number;
  online: number;
  offline: number;
  max_onus: number;
  fill_pct: number;
  online_fill_pct: number;
  offline_fill_pct: number;
  up_free_gbps: number;
  down_free_gbps: number;
  up_cap_gbps: number;
  down_cap_gbps: number;
  status: "ok" | "oversubscribed" | string;
};

export type SmartOLTCapacity = {
  ports: SmartOLTCapacityPort[];
  summary: {
    pon_ports: number;
    onus: number;
    online: number;
    offline: number;
    avg_fill: number;
    over_threshold: number;
    oversub_pct: number;
  };
  max_onus: number;
  oversub_pct: number;
};

export type SmartOLTMapOnu = {
  id: string;
  kind: "onu";
  onu_external_id: string;
  name: string;
  sn?: string | null;
  status?: string | null;
  status_kind: SmartOLTStatusKind;
  signal_rx?: number | null;
  signal_band: "strong" | "weak" | "";
  latitude: number;
  longitude: number;
  olt_id?: string | null;
  olt_name?: string | null;
  board?: string | null;
  port?: string | null;
  zone_name?: string | null;
  odb_name?: string | null;
  address?: string | null;
  gps_source?: "smartolt" | "customer" | string | null;
  customer_id?: string | null;
};

export type SmartOLTMapOdb = {
  id: string;
  kind: "odb";
  odb_id: string;
  name: string;
  latitude: number;
  longitude: number;
  zone_id?: string | null;
  zone_name?: string | null;
};

export type SmartOLTMap = {
  onus: SmartOLTMapOnu[];
  odbs: SmartOLTMapOdb[];
  summary: {
    onus: number;
    odbs: number;
    online: number;
    los: number;
    power: number;
    other: number;
    weak: number;
    strong: number;
    onus_without_gps: number;
    odbs_without_gps: number;
  };
};

export type SmartOLTOperationSchema = {
  key: string;
  method: "GET" | "POST";
  path: string;
  category: string;
  path_params: string[];
  query_params: string[];
  form_fields: string[];
  bulk: boolean;
  critical: boolean;
  display_name: string;
  has_default_path: boolean;
  has_tenant_override: boolean;
};

export type SmartOLTOpticalHealth = {
  status: "optimal" | "warning" | "critical" | "unknown";
  label: string;
  color: "emerald" | "amber" | "rose" | "gray";
  message: string;
  rx_power: number | null;
};

export type SmartOLTWifiPort = {
  port: string;
  ssid?: string | null;
  password?: string | null;
  auth_mode?: string | null;
  admin_state?: string | null;
  mode?: string | null;
  vlan?: string | null;
};

export type SmartOLTEthernetPort = {
  port: string;
  admin_state?: string | null;
  mode?: string | null;
  vlan?: string | null;
  dhcp?: string | null;
};

export type SmartOLTServicePort = {
  service_port?: string | null;
  vlan?: string | null;
  cvlan?: string | null;
  svlan?: string | null;
  upload_speed?: string | null;
  download_speed?: string | null;
  tag_transform_mode?: string | null;
};

export type SmartOLTWanInfo = {
  mode?: string | null;
  pppoe_username?: string | null;
  vlan?: string | null;
  mgmt_ip_address?: string | null;
  mgmt_ip_subnet_mask?: string | null;
  mgmt_ip_default_gateway?: string | null;
  mgmt_ip_dns1?: string | null;
  mgmt_ip_dns2?: string | null;
  mgmt_ip_mode?: string | null;
};

export type CustomerSmartOLTProfile = {
  customer: {
    id: string;
    internet_id: string;
    name: string;
  };
  mapping: {
    exists: boolean;
    id: string | null;
    onu_external_id: string | null;
    sn: string | null;
    mac: string | null;
    mapping_status: string | null;
    validated_at: string | null;
  };
  suggested_mapping?: {
    customer_id: string;
    onu_external_id: string;
    sn: string | null;
    mac: string | null;
    reason: string;
  } | null;
  cards: {
    status: string | null;
    admin_status: string | null;
    catv_status: string | null;
    signal_rx: number | null;
    signal_tx: number | null;
    signal_quality: string | null;
    signal_1490: string | number | null;
    signal_1310: string | number | null;
    optical_health?: SmartOLTOpticalHealth;
    distance?: number | null;
    onu_name?: string | null;
    onu_type_name?: string | null;
    olt_id?: string | null;
    olt_name?: string | null;
    board?: string | null;
    port?: string | null;
    onu_number?: string | null;
    zone_id?: string | null;
    zone_name?: string | null;
    odb_id?: string | null;
    odb_name?: string | null;
    bandwidth?: string | null;
    mgmt_ip?: string | null;
    pppoe_username?: string | null;
    vlan?: string | null;
    last_sync_at: string | null;
    sync_age_minutes: number | null;
  };
  configuration: {
    wifi_ports?: SmartOLTWifiPort[];
    ethernet_ports?: SmartOLTEthernetPort[];
    service_ports?: SmartOLTServicePort[];
    wan_info?: SmartOLTWanInfo;
    onu_type_name?: string | null;
    distance_meters?: string | number | null;
    speed_profile?: any;
    vlans?: any;
    wan?: any;
    mgmt_ip?: any;
    tr069?: any;
    voip?: any;
    iptv?: any;
    catv?: any;
    port_modes?: any;
    l2_l3_guard?: any;
  };
  raw_configuration?: Record<string, any>;
  stale?: {
    status_is_stale: boolean;
    signal_is_stale: boolean;
  };
};

export interface LiveTrafficTestSession {
  session_id: string;
  target_name: string;
  target_identifier: string;
  wan_ip?: string | null;
  provisioned_down_mbps: number;
  provisioned_up_mbps: number;
  duration_seconds: number;
  is_active: boolean;
  started_at: number;
}

export interface LiveTrafficSample {
  session_id: string;
  elapsed_seconds: number;
  remaining_seconds: number;
  is_active: boolean;
  download_mbps: number;
  upload_mbps: number;
  download_peak_mbps: number;
  upload_peak_mbps: number;
  download_avg_mbps: number;
  upload_avg_mbps: number;
  total_bytes_transferred: number;
  latency_ms: number;
  wan_ip?: string | null;
  target_identifier?: string | null;
  completed?: boolean;
}

export interface SmartOLTTopology {
  zones: Array<{ zone_id: string; name: string }>;
  odbs: Array<{ odb_id: string; name: string; zone_id?: string }>;
  vlans: Array<{ vlan_id: string; name: string; olt_id?: string }>;
  speed_profiles: Array<{ profile_id: string; name: string }>;
  onu_types: Array<{ smartolt_type_id: string; name: string; pon_type?: string; capability?: string }>;
}

export type SmartOLTLiveTopologyOnu = {
  onu_external_id: string;
  name: string;
  status_kind: SmartOLTStatusKind;
  signal_rx?: number | null;
  board?: string | null;
  port?: string | null;
  zone_name?: string | null;
  fetched_at?: string | null;
};

export type SmartOLTLiveTopologyPort = {
  key: string;
  board: string;
  port: string;
  label: string;
  pon_name?: string | null;
  pon_type?: string | null;
  onus: number;
  online: number;
  nodes: SmartOLTLiveTopologyOnu[];
};

export type SmartOLTLiveTopologyEvent = {
  time?: string | null;
  type: "connected" | "los" | "power" | "other";
  label: string;
  olt_name?: string | null;
  onu_external_id?: string | null;
  onu_name?: string | null;
  message: string;
};

export type SmartOLTLiveTopologyOlt = {
  olt_id: string;
  name: string;
  reachable: boolean;
  online: number;
  total: number;
  has_layout: boolean;
  map_markers: number;
};

export type SmartOLTLiveTopology = {
  olts: SmartOLTLiveTopologyOlt[];
  selected_olt_id?: string | null;
  has_layout: boolean;
  map_markers: number;
  reachable: boolean;
  online: number;
  total: number;
  ports: SmartOLTLiveTopologyPort[];
  events: SmartOLTLiveTopologyEvent[];
  updated_at?: string | null;
};

export type SmartOLTXrtgDeviceOnu = {
  onu_external_id: string;
  name: string;
  label: string;
  status_kind: SmartOLTStatusKind;
  signal_rx?: number | null;
  board?: string | null;
  port?: string | null;
};

export type SmartOLTXrtgDeviceOlt = {
  olt_id: string;
  name: string;
  count: number;
  onus: SmartOLTXrtgDeviceOnu[];
};

export type SmartOLTXrtgDevices = {
  olts: SmartOLTXrtgDeviceOlt[];
  total_onus: number;
};

export type SmartOLTXrtgSeries = {
  onu_external_id: string;
  kind: "signal" | "traffic";
  range: string;
  graph_type: string;
  source?: "history" | "current_snapshot" | "zero_baseline" | "empty" | string;
  message?: string | null;
  series: {
    signal_rx: Array<{ ts: string; value: number }>;
    signal_tx: Array<{ ts: string; value: number }>;
    traffic_up: Array<{ ts: string; value: number }>;
    traffic_down: Array<{ ts: string; value: number }>;
  };
};

export type SmartOLTDiagnosticsEvent = {
  id: string;
  olt_id?: string | null;
  olt_name?: string | null;
  onu_external_id?: string | null;
  onu_name?: string | null;
  ip_address?: string | null;
  date?: string | null;
  message: string;
  severity: string;
  tone?: "online" | "critical" | "warning" | "neutral" | string;
  source?: string | null;
};

export type SmartOLTDiagnosticsTab =
  | "emergency_critical"
  | "major"
  | "medium"
  | "minor"
  | "unknown";

export type SmartOLTDiagnostics = {
  tab: SmartOLTDiagnosticsTab;
  split_view: boolean;
  critical_events: SmartOLTDiagnosticsEvent[];
  warning_events: SmartOLTDiagnosticsEvent[];
  events: SmartOLTDiagnosticsEvent[];
  reload_seconds: number;
  updated_at?: string | null;
  counts: Record<string, number>;
  filtered_count: number;
};

export type SmartOLTReportsOutage = {
  onu_external_id: string;
  onu_name: string;
  olt_id?: string | null;
  olt_name?: string | null;
  port?: string | null;
  status_kind: string;
  cause: string;
  disconnect_count: number;
  signal_rx?: number | null;
  signal_tx?: number | null;
  distance_m?: number | null;
};

export type SmartOLTReportsScatterPoint = {
  onu_external_id: string;
  name: string;
  km: number;
  rx: number;
  status_kind: string;
  band: string;
};

export type SmartOLTReportsPonAverage = {
  olt_id?: string | null;
  olt_name?: string | null;
  board?: string | null;
  port?: string | null;
  avg_rx: number;
  onus: number;
};

export type SmartOLTZoneRow = {
  zone_id: string;
  name: string;
  onus: number;
  odbs: number;
  fetched_at?: string | null;
};

export type SmartOLTZones = {
  zones: SmartOLTZoneRow[];
  summary: {
    total: number;
    unassigned_onus: number;
    total_onus: number;
    total_odbs: number;
  };
  updated_at?: string | null;
};

export type SmartOLTOdbRow = {
  odb_id: string;
  name: string;
  zone_id?: string | null;
  zone_name?: string | null;
  onus: number;
  has_gps: boolean;
  fetched_at?: string | null;
};

export type SmartOLTOdbs = {
  items: SmartOLTOdbRow[];
  summary: { total: number; with_gps: number; total_onus_linked: number };
  updated_at?: string | null;
};

export type SmartOLTOnuTypeRow = {
  smartolt_type_id: string;
  name: string;
  pon_type: string;
  capabilities: string;
  onus: number;
  fetched_at?: string | null;
};

export type SmartOLTOnuTypes = {
  items: SmartOLTOnuTypeRow[];
  summary: { total: number; total_onus: number };
  updated_at?: string | null;
};

export type SmartOLTSpeedProfileRow = {
  profile_id: string;
  name: string;
  upload?: string | null;
  download?: string | null;
  fetched_at?: string | null;
};

export type SmartOLTSpeedProfiles = {
  items: SmartOLTSpeedProfileRow[];
  summary: { total: number };
  updated_at?: string | null;
};

export type SmartOLTOltSettingsRow = {
  olt_id: string;
  name: string;
  ip?: string | null;
  hardware_version?: string | null;
  uptime?: string | null;
  env_temp?: string | null;
  reachable: boolean;
  onus: number;
  online: number;
  fetched_at?: string | null;
};

export type SmartOLTOltSettings = {
  items: SmartOLTOltSettingsRow[];
  summary: { total: number; reachable: number; stale: number };
  updated_at?: string | null;
};

export type SmartOLTOltMgmtIpRow = {
  onu_external_id: string;
  name: string;
  ip_address?: string | null;
  subnet_mask?: string | null;
  default_gateway?: string | null;
  dns1?: string | null;
  dns2?: string | null;
  mgmt_vlan?: string | null;
  wan_mode?: string | null;
  mgmt_ip_mode?: string | null;
};

export type SmartOLTOltMgmtIps = {
  olt_id: string;
  pools: Array<{
    ip_address?: string | null;
    subnet_mask?: string | null;
    default_gateway?: string | null;
    dns1?: string | null;
    dns2?: string | null;
    assigned_count?: number;
    usage_label?: string;
  }>;
  assigned: SmartOLTOltMgmtIpRow[];
  summary: { assigned: number; total_onus: number };
};

export type SmartOLTSystemSettings = {
  tenant: {
    enabled: boolean;
    subdomain?: string | null;
    base_url?: string | null;
    budget_per_hour: number;
    can_manage_module_access?: boolean;
  };
  thresholds: { capacity_max_onus: number; capacity_oversub_pct: number };
  policy: { auto_suspend_overdue: boolean };
  sync: {
    jobs: Array<{
      job_key: string;
      status: string;
      interval_seconds: number;
      last_run_at?: string | null;
      next_run_at?: string | null;
      error_count: number;
      last_error?: string | null;
    }>;
    status_counts: Record<string, number>;
  };
  budget: { used_calls?: number; budget_per_hour?: number; remaining?: number };
  updated_at?: string | null;
};

export type SmartOLTReports = {
  selected_olt_id: string;
  olts: Array<{ olt_id: string; name: string; uptime_label: string; env_temp?: string | null }>;
  services: Array<{ id: string; label: string; status: string }>;
  summary: {
    total: number;
    online: number;
    offline: number;
    pwr: number;
    los: number;
    other: number;
    good: number;
    warning: number;
    bad: number;
    autofind: { epon: number; gpon: number; total: number };
  };
  recurring_outages: SmartOLTReportsOutage[];
  scatter_points: SmartOLTReportsScatterPoint[];
  pon_averages: SmartOLTReportsPonAverage[];
  alarm_levels: { critical: number; major: number; minor: number; total: number };
  command_levels: { critical: number; major: number; minor: number; total: number };
  updated_at?: string | null;
};

export const smartoltService = {
  getTenantConfig: async (): Promise<SmartOLTTenantConfig> => {
    const resp = await axiosInstance.get("/api/smartolt/tenant-config");
    return resp.data;
  },
  updateTenantConfig: async (payload: SmartOLTTenantConfigUpdate): Promise<SmartOLTTenantConfig> => {
    const resp = await axiosInstance.put("/api/smartolt/tenant-config", payload);
    return resp.data;
  },
  getOverview: async (): Promise<SmartOLTOverview> => {
    const resp = await axiosInstance.get("/api/smartolt/overview");
    return resp.data;
  },
  getOlts: async (): Promise<SmartOLTOltRow[]> => {
    const resp = await axiosInstance.get("/api/smartolt/olts");
    return resp.data;
  },
  getHealth: async (verifyRemote = false): Promise<SmartOLTHealth> => {
    const resp = await axiosInstance.get("/api/smartolt/health", {
      params: verifyRemote ? { verify: "true" } : undefined,
    });
    return resp.data;
  },
  getOnus: async (params?: {
    status?: string;
    q?: string;
    limit?: number;
    db_status?: string;
    mapped?: string;
  }): Promise<SmartOLTOnuRow[]> => {
    const resp = await axiosInstance.get("/api/smartolt/onus", { params });
    return resp.data;
  },

  // GAP-02: Unconfigured ONUs — physically connected but not provisioned
  getUnconfiguredOnus: async (params?: { olt_id?: string; limit?: number }): Promise<SmartOLTUnconfiguredOnuRow[]> => {
    const resp = await axiosInstance.get("/api/smartolt/onus/unconfigured", { params });
    return resp.data;
  },

  getTerminatedOnus: async (params?: { limit?: number }): Promise<SmartOLTOnuRow[]> => {
    const resp = await axiosInstance.get("/api/smartolt/onus/terminated", { params });
    return resp.data;
  },

  getCapacity: async (): Promise<SmartOLTCapacity> => {
    const resp = await axiosInstance.get("/api/smartolt/capacity");
    return resp.data;
  },

  getMap: async (): Promise<SmartOLTMap> => {
    const resp = await axiosInstance.get("/api/smartolt/map");
    return resp.data;
  },

  getAlarms: async (params?: {
    severity?: string;
    state?: string;
    range?: string;
    q?: string;
  }): Promise<SmartOLTAlarmList> => {
    const resp = await axiosInstance.get("/api/smartolt/alarms", { params });
    return resp.data;
  },

  updateAlarms: async (action: "ack" | "clear" | "notify", ids: string[]) => {
    const resp = await axiosInstance.post("/api/smartolt/alarms/actions", { action, ids });
    return resp.data;
  },

  getOnuLiveDetail: async (
    onuExternalId: string,
    opts?: { force?: boolean; cooldown_seconds?: number },
  ): Promise<{
    ok: boolean;
    cached: boolean;
    live: boolean;
    degraded?: boolean;
    source?: string;
    upstream_error?: string | null;
    onu_external_id: string;
    age_seconds?: number;
    cooldown_remaining?: number;
    fetched_at: string;
    data: any;
  }> => {
    const resp = await axiosInstance.get(`/api/smartolt/onus/${encodeURIComponent(onuExternalId)}/live-detail`, {
      params: opts,
    });
    return resp.data;
  },

  getOnuWebAdminLink: async (onuExternalId: string): Promise<SmartOLTOnuWebAdminLink> => {
    const resp = await axiosInstance.get(`/api/smartolt/onus/${encodeURIComponent(onuExternalId)}/web-admin-link`);
    return resp.data;
  },

  getOltDetail: async (oltId: string) => {
    const resp = await axiosInstance.get(`/api/smartolt/olts/${encodeURIComponent(oltId)}/detail`);
    return resp.data;
  },
  getOltMgmtIps: async (oltId: string): Promise<SmartOLTOltMgmtIps> => {
    const resp = await axiosInstance.get(`/api/smartolt/olts/${encodeURIComponent(oltId)}/mgmt-ips`);
    return resp.data;
  },
  refreshOltSection: async (oltId: string, section: string) => {
    const resp = await axiosInstance.post(`/api/smartolt/olts/${encodeURIComponent(oltId)}/refresh`, { section });
    return resp.data;
  },
  getTopology: async (): Promise<SmartOLTTopology> => {
    const resp = await axiosInstance.get("/api/smartolt/topology");
    return resp.data;
  },

  getLiveTopology: async (oltId?: string): Promise<SmartOLTLiveTopology> => {
    const resp = await axiosInstance.get("/api/smartolt/live-topology", {
      params: oltId ? { olt_id: oltId } : undefined,
    });
    return resp.data;
  },

  getXrtgDevices: async (q?: string): Promise<SmartOLTXrtgDevices> => {
    const resp = await axiosInstance.get("/api/smartolt/xrtg/devices", { params: q ? { q } : undefined });
    return resp.data;
  },

  getXrtgSeries: async (
    onuExternalId: string,
    params?: { kind?: "signal" | "traffic"; range?: string },
  ): Promise<SmartOLTXrtgSeries> => {
    const resp = await axiosInstance.get("/api/smartolt/xrtg/series", {
      params: { onu_external_id: onuExternalId, kind: params?.kind || "signal", range: params?.range || "1hr" },
    });
    return resp.data;
  },

  getXrtgGraphImage: async (
    onuExternalId: string,
    params?: { kind?: "signal" | "traffic"; range?: string },
  ): Promise<Blob> => {
    const resp = await axiosInstance.get("/api/smartolt/xrtg/graph-image", {
      params: { onu_external_id: onuExternalId, kind: params?.kind || "traffic", range: params?.range || "1hr" },
      responseType: "blob",
      skipErrorToast: true,
    });
    return resp.data;
  },

  getDiagnostics: async (params?: {
    tab?: SmartOLTDiagnosticsTab;
    q?: string;
    range?: string;
  }): Promise<SmartOLTDiagnostics> => {
    const resp = await axiosInstance.get("/api/smartolt/diagnostics", { params });
    return resp.data;
  },

  getReports: async (oltId?: string): Promise<SmartOLTReports> => {
    const resp = await axiosInstance.get("/api/smartolt/reports", {
      params: oltId && oltId !== "all" ? { olt_id: oltId } : undefined,
    });
    return resp.data;
  },

  getZones: async (): Promise<SmartOLTZones> => {
    const resp = await axiosInstance.get("/api/smartolt/zones");
    return resp.data;
  },

  getOdbs: async (): Promise<SmartOLTOdbs> => {
    const resp = await axiosInstance.get("/api/smartolt/odbs");
    return resp.data;
  },

  getOnuTypes: async (): Promise<SmartOLTOnuTypes> => {
    const resp = await axiosInstance.get("/api/smartolt/onu-types");
    return resp.data;
  },

  getSpeedProfiles: async (): Promise<SmartOLTSpeedProfiles> => {
    const resp = await axiosInstance.get("/api/smartolt/speed-profiles");
    return resp.data;
  },

  getOltSettings: async (): Promise<SmartOLTOltSettings> => {
    const resp = await axiosInstance.get("/api/smartolt/settings/olts");
    return resp.data;
  },

  getSystemSettings: async (): Promise<SmartOLTSystemSettings> => {
    const resp = await axiosInstance.get("/api/smartolt/settings/system");
    return resp.data;
  },

  updateCapacityThresholds: async (payload: { capacity_max_onus?: number; capacity_oversub_pct?: number }) => {
    const resp = await axiosInstance.put("/api/smartolt/settings/thresholds", payload);
    return resp.data;
  },

  getCommands: async (limit = 100): Promise<SmartOLTCommandRow[]> => {
    const resp = await axiosInstance.get("/api/smartolt/commands", { params: { limit } });
    return resp.data;
  },
  getSyncJobs: async () => {
    const resp = await axiosInstance.get("/api/smartolt/sync/jobs");
    return resp.data;
  },
  getBudget: async () => {
    const resp = await axiosInstance.get("/api/smartolt/sync/budget");
    return resp.data;
  },
  getOperations: async (): Promise<{ total_operations: number; operations: SmartOLTOperationSchema[] }> => {
    const resp = await axiosInstance.get("/api/smartolt/operations");
    return resp.data;
  },
  seedSyncJobs: async () => {
    const resp = await axiosInstance.post("/api/smartolt/sync/seed-jobs");
    return resp.data;
  },
  runSyncNow: async (limit = 25) => {
    const resp = await axiosInstance.post("/api/smartolt/sync/run-now", undefined, { params: { limit } });
    return resp.data;
  },
  createCommand: async (operation_key: string, payload: Record<string, unknown>, idempotency_key?: string) => {
    const resp = await axiosInstance.post("/api/smartolt/commands", {
      operation_key,
      payload,
      idempotency_key,
    });
    return resp.data;
  },
  readOperation: async (operation_key: string, payload: Record<string, unknown>) => {
    const resp = await axiosInstance.post("/api/smartolt/read", {
      operation_key,
      payload,
    });
    return resp.data;
  },
  retryCommand: async (commandId: string) => {
    const resp = await axiosInstance.post(`/api/smartolt/commands/${commandId}/retry`);
    return resp.data;
  },
  runCommandsNow: async (limit = 10) => {
    const resp = await axiosInstance.post("/api/smartolt/commands/run-now", undefined, { params: { limit } });
    return resp.data;
  },

  // ─── Customer integration ─────────────────────────────────────────────────

  getCustomerProfile: async (customerId: string): Promise<CustomerSmartOLTProfile> => {
    const resp = await axiosInstance.get(`/api/smartolt/customers/${customerId}/profile`);
    return resp.data;
  },
  updateCustomerMapping: async (
    customerId: string,
    payload: { onu_external_id: string; sn?: string; mac?: string; force_remap?: boolean },
  ) => {
    const resp = await axiosInstance.put(`/api/smartolt/customers/${customerId}/mapping`, payload);
    return resp.data;
  },
  refreshCustomerFromSmartOLT: async (
    customerId: string,
    opts?: { force?: boolean; cooldown_seconds?: number },
  ) => {
    const resp = await axiosInstance.post(`/api/smartolt/customers/${customerId}/refresh`, opts || {});
    return resp.data;
  },
  getCustomerGraphs: async (customerId: string, range: "6h" | "24h" | "7d" | "30d" = "24h") => {
    const resp = await axiosInstance.get(`/api/smartolt/customers/${customerId}/graphs`, { params: { range } });
    return resp.data;
  },
  getCustomerGraphImage: async (
    customerId: string,
    params: { kind?: "signal" | "traffic"; graph_type?: "hourly" | "daily" | "weekly" | "monthly" | "yearly" } = {},
  ) => {
    const resp = await axiosInstance.get(`/api/smartolt/customers/${customerId}/graph-image`, {
      params: {
        kind: params.kind || "signal",
        graph_type: params.graph_type || "hourly",
      },
      responseType: "blob",
      skipErrorToast: true,
    });
    return URL.createObjectURL(resp.data);
  },
  autoLinkCustomerMappings: async (opts?: { dry_run?: boolean; force_remap?: boolean }) => {
    const resp = await axiosInstance.post("/api/smartolt/mappings/auto-link", opts || {});
    return resp.data;
  },
  syncOnuMappings: async () => {
    const resp = await axiosInstance.post<{
      company_id: string;
      total_customers: number;
      total_onus: number;
      newly_mapped: number;
      corrected_mappings: number;
      already_correct: number;
      skipped_no_match: number;
      skipped_ambiguous: number;
      message: string;
      details: any[];
    }>("/api/smartolt/mappings/sync");
    return resp.data;
  },
  getCustomerCommands: async (customerId: string) => {
    const resp = await axiosInstance.get(`/api/smartolt/customers/${customerId}/commands`);
    return resp.data;
  },
  submitCustomerAction: async (customerId: string, operation_key: string, payload?: Record<string, unknown>) => {
    const resp = await axiosInstance.post(`/api/smartolt/customers/${customerId}/actions`, {
      operation_key,
      payload: payload || {},
    });
    return resp.data;
  },

  // ─── On-Demand Live Traffic & Speed Test ──────────────────────────────────

  startLiveTrafficTest: async (params: {
    customerId?: string;
    onuExternalId?: string;
    durationSeconds?: number;
  }): Promise<LiveTrafficTestSession> => {
    const resp = await axiosInstance.post("/api/smartolt/traffic-test/start", {
      customer_id: params.customerId,
      onu_external_id: params.onuExternalId,
      duration_seconds: params.durationSeconds || 30,
    });
    return resp.data;
  },

  getLiveTrafficSample: async (sessionId: string): Promise<LiveTrafficSample> => {
    const resp = await axiosInstance.get(`/api/smartolt/traffic-test/${encodeURIComponent(sessionId)}/sample`);
    return resp.data;
  },

  stopLiveTrafficTest: async (sessionId: string): Promise<LiveTrafficSample> => {
    const resp = await axiosInstance.post(`/api/smartolt/traffic-test/${encodeURIComponent(sessionId)}/stop`);
    return resp.data;
  },

  // ─── Complaint & Task SmartOLT integration ────────────────────────────────

  getComplaintDiagnostics: async (complaintId: string) => {
    const resp = await axiosInstance.get(`/complaints/${complaintId}/smartolt-diagnostics`);
    return resp.data;
  },
  submitComplaintAction: async (complaintId: string, operation_key: string, payload?: Record<string, unknown>) => {
    const resp = await axiosInstance.post(`/complaints/${complaintId}/smartolt-actions`, {
      operation_key,
      payload: payload || {},
    });
    return resp.data;
  },
  submitTaskAction: async (taskId: string, operation_key: string, payload?: Record<string, unknown>) => {
    const resp = await axiosInstance.post(`/tasks/${taskId}/smartolt-actions`, {
      operation_key,
      payload: payload || {},
    });
    return resp.data;
  },

  // ─── GAP-04: Authorization Presets ───────────────────────────────────────

  /**
   * Fetches authorization presets for an OLT (and optionally filtered by SN).
   * Live read from SmartOLT — not cached locally.
   * SmartOLT: GET /api/onu/get_authorization_presets
   */
  getAuthorizationPresets: async (params: {
    olt_id: string;
    sn?: string;
    board?: string;
    port?: string;
  }) => {
    const operation_key = params.sn
      ? "get_onu_authorization_presets_by_sn"
      : "get_onu_authorization_presets_by_olt_id";
    const resp = await axiosInstance.post("/api/smartolt/read", {
      operation_key,
      payload: {
        query_params: {
          olt_id: params.olt_id,
          ...(params.sn ? { sn: params.sn } : {}),
          ...(params.board ? { board: params.board } : {}),
          ...(params.port ? { port: params.port } : {}),
        },
      },
    });
    return resp.data;
  },

  /**
   * Authorize an unconfigured ONU using a preset (1-click provisioning).
   * SmartOLT: POST /api/onu/authorize_onu (preset-based form)
   */
  authorizeOnuWithPreset: async (formData: {
    preset_id: string;
    onu_external_id?: string;
    [key: string]: string | undefined;
  }) => {
    const resp = await axiosInstance.post("/api/smartolt/commands", {
      operation_key: "authorize_onu_using_authorization_preset",
      payload: { form_data: formData },
    });
    return resp.data;
  },

  /**
   * Authorize an unconfigured ONU using the full manual form.
   * SmartOLT: POST /api/onu/authorize_onu
   */
  authorizeOnu: async (formData: Record<string, string>) => {
    const resp = await axiosInstance.post("/api/smartolt/commands", {
      operation_key: "authorize_onu",
      payload: { form_data: formData },
    });
    return resp.data;
  },

  // ─── GAP-06: Configuration Presets ───────────────────────────────────────

  /**
   * Fetches all available configuration presets (live read from SmartOLT).
   * SmartOLT: GET /api/onu/get_configuration_presets
   */
  getConfigurationPresets: async () => {
    const resp = await axiosInstance.post("/api/smartolt/read", {
      operation_key: "get_onu_configuration_presets",
      payload: {},
    });
    return resp.data;
  },

  /**
   * Apply a configuration preset to a single ONU (queued async command).
   * SmartOLT: POST /api/onu/apply_configuration_preset/{onu_external_id}
   */
  applyConfigurationPreset: async (onuExternalId: string, formData?: Record<string, string>) => {
    const resp = await axiosInstance.post("/api/smartolt/commands", {
      operation_key: "apply_configuration_preset_to_onu_by_external_id",
      payload: {
        path_params: { onu_external_id: onuExternalId },
        form_data: formData || {},
      },
    });
    return resp.data;
  },

  /**
   * Bulk apply a configuration preset to multiple ONUs.
   * SmartOLT: POST /api/onu/bulk_apply_configuration_preset
   * NOTE: SmartOLT processes this asynchronously. Poll checkConfigPresetTaskStatus() with the returned task_id.
   */
  bulkApplyConfigurationPreset: async (onuExternalIds: string[], formData?: Record<string, string>) => {
    const resp = await axiosInstance.post("/api/smartolt/commands", {
      operation_key: "apply_configuration_preset_on_multiple_onus",
      payload: {
        form_data: {
          onus_external_ids: onuExternalIds.join(","),
          ...(formData || {}),
        },
      },
    });
    return resp.data;
  },

  /**
   * Remove a configuration preset from a single ONU.
   * SmartOLT: POST /api/onu/remove_configuration_preset/{onu_external_id}
   */
  removeConfigurationPreset: async (onuExternalId: string) => {
    const resp = await axiosInstance.post("/api/smartolt/commands", {
      operation_key: "remove_configuration_preset_from_onu_by_external_id",
      payload: { path_params: { onu_external_id: onuExternalId } },
    });
    return resp.data;
  },

  bulkRemoveConfigurationPreset: async (onuExternalIds: string[]) => {
    const resp = await axiosInstance.post("/api/smartolt/commands", {
      operation_key: "remove_configuration_preset_from_multiple_onus",
      payload: { form_data: { onus_external_ids: onuExternalIds.join(",") } },
    });
    return resp.data;
  },

  setOnuMgmtIpInactive: async (onuExternalId: string) => {
    const resp = await axiosInstance.post("/api/smartolt/commands", {
      operation_key: "set_onu_mgmt_ip_mode_inactive_by_external_id",
      payload: { path_params: { onu_external_id: onuExternalId } },
    });
    return resp.data;
  },

  setOnuMgmtIpStatic: async (onuExternalId: string, formData: Record<string, string>) => {
    const resp = await axiosInstance.post("/api/smartolt/commands", {
      operation_key: "set_onu_mgmt_ip_mode_static_ip_by_external_id",
      payload: {
        path_params: { onu_external_id: onuExternalId },
        form_data: formData,
      },
    });
    return resp.data;
  },

  setOnuMgmtIpDhcp: async (onuExternalId: string, formData: Record<string, string>) => {
    const resp = await axiosInstance.post("/api/smartolt/commands", {
      operation_key: "set_onu_mgmt_ip_mode_dhcp_by_external_id",
      payload: {
        path_params: { onu_external_id: onuExternalId },
        form_data: formData,
      },
    });
    return resp.data;
  },

  /**
   * Poll the status of an async bulk configuration preset task.
   * SmartOLT: POST /api/onu/check_configuration_preset_task_status
   * Call every ~3 seconds after bulkApplyConfigurationPreset until status is 'done' or 'error'.
   */
  checkConfigPresetTaskStatus: async (taskId: string) => {
    const resp = await axiosInstance.post("/api/smartolt/read", {
      operation_key: "check_configuration_preset_task_status",
      payload: { form_data: { task_id: taskId } },
    });
    return resp.data;
  },

  pollConfigurationPresetTask: async (
    taskId: string,
    onProgress?: (progress: any) => void,
    maxAttempts = 15,
    intervalMs = 3000,
  ): Promise<any> => {
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const result = await smartoltService.checkConfigPresetTaskStatus(taskId);
      onProgress?.(result);
      const status =
        result?.response?.status ||
        result?.status ||
        result?.data?.status ||
        result?.response?.task_status;
      if (["done", "completed", "error", "failed"].includes(String(status || "").toLowerCase())) {
        return result;
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
    throw new Error("Configuration preset task timed out");
  },

  // ─── GAP-05: Bulk ONU Operations ─────────────────────────────────────────

  /**
   * Bulk update speed profiles for multiple ONUs (max 50 per SmartOLT limit).
   * SmartOLT: POST /api/onu/bulk_update_speed_profiles
   */
  bulkUpdateSpeedProfiles: async (
    onuExternalIds: string[],
    uploadProfile: string,
    downloadProfile: string,
  ) => {
    const resp = await axiosInstance.post("/api/smartolt/commands", {
      operation_key: "update_multiple_onus_speed_profiles_by_external_ids",
      payload: {
        form_data: {
          onus_external_ids: onuExternalIds.join(","),
          upload_speed_profile_name: uploadProfile,
          download_speed_profile_name: downloadProfile,
        },
      },
    });
    return resp.data;
  },

  /** Bulk enable multiple ONUs (max 50). SmartOLT: POST /api/onu/bulk_enable */
  bulkEnableOnus: async (onuExternalIds: string[]) => {
    const resp = await axiosInstance.post("/api/smartolt/commands", {
      operation_key: "bulk_enable_onus_by_external_ids",
      payload: { form_data: { onus_external_ids: onuExternalIds.join(",") } },
    });
    return resp.data;
  },

  /** Bulk disable multiple ONUs (max 50). SmartOLT: POST /api/onu/bulk_disable */
  bulkDisableOnus: async (onuExternalIds: string[]) => {
    const resp = await axiosInstance.post("/api/smartolt/commands", {
      operation_key: "bulk_disable_onus_by_external_ids",
      payload: { form_data: { onus_external_ids: onuExternalIds.join(",") } },
    });
    return resp.data;
  },

  /** Bulk enable CATV on multiple ONUs (max 50). SmartOLT: POST /api/onu/bulk_enable_catv */
  bulkEnableCatv: async (onuExternalIds: string[]) => {
    const resp = await axiosInstance.post("/api/smartolt/commands", {
      operation_key: "bulk_enable_catv_on_multiple_onus",
      payload: { form_data: { onus_external_ids: onuExternalIds.join(",") } },
    });
    return resp.data;
  },

  /** Bulk disable CATV on multiple ONUs (max 50). SmartOLT: POST /api/onu/bulk_disable_catv */
  bulkDisableCatv: async (onuExternalIds: string[]) => {
    const resp = await axiosInstance.post("/api/smartolt/commands", {
      operation_key: "bulk_disable_catv_on_multiple_onus",
      payload: { form_data: { onus_external_ids: onuExternalIds.join(",") } },
    });
    return resp.data;
  },

  // ─── Automation ───────────────────────────────────────────────────────────

  getAutomationPolicy: async () => {
    const resp = await axiosInstance.get("/api/smartolt/automation/policy");
    return resp.data;
  },
  updateAutomationPolicy: async (smartolt_policy: { auto_suspend_overdue: boolean }) => {
    const resp = await axiosInstance.put("/api/smartolt/automation/policy", { smartolt_policy });
    return resp.data;
  },
  evaluateCustomerPolicy: async (customerId: string) => {
    const resp = await axiosInstance.post(`/api/smartolt/automation/evaluate-customer/${customerId}`);
    return resp.data;
  },

  // ─── Billing Suspensions & Overrides ───────────────────────────────────────

  getBillingSuspensions: async (params?: { status?: string; search?: string; page?: number; per_page?: number }) => {
    const resp = await axiosInstance.get<{ total: number; page: number; per_page: number; suspensions: SmartOLTBillingSuspensionRow[] }>(
      "/api/smartolt/billing-suspensions",
      { params }
    );
    return resp.data;
  },

  getBillingSuspensionStats: async () => {
    const resp = await axiosInstance.get<SmartOLTBillingSuspensionStats>("/api/smartolt/billing-suspensions/stats");
    return resp.data;
  },

  reactivateBillingSuspension: async (suspensionId: string, reason?: string, durationDays = 5) => {
    const resp = await axiosInstance.post(`/api/smartolt/billing-suspensions/${encodeURIComponent(suspensionId)}/reactivate`, {
      reason,
      duration_days: durationDays,
    });
    return resp.data;
  },

  bulkReactivateBillingSuspensions: async (
    items: Array<{ suspension_id: string; duration_days: number; reason?: string }>,
    reason?: string
  ) => {
    const resp = await axiosInstance.post<{
      message: string;
      reactivated_count: number;
      failed_count: number;
      reactivated: any[];
      errors: any[];
    }>("/api/smartolt/billing-suspensions/bulk-reactivate", {
      items,
      reason,
    });
    return resp.data;
  },

  resuspendBillingSuspension: async (suspensionId: string, reason?: string) => {
    const resp = await axiosInstance.post(`/api/smartolt/billing-suspensions/${encodeURIComponent(suspensionId)}/re-suspend`, {
      reason,
    });
    return resp.data;
  },

  runBillingWorkerNow: async () => {
    const resp = await axiosInstance.post<{ message: string; stats: Record<string, number> }>(
      "/api/smartolt/billing-suspensions/run-worker-now"
    );
    return resp.data;
  },
};

export type SmartOLTBillingSuspensionRow = {
  id: string;
  customer_id: string;
  customer_name: string;
  internet_id: string;
  phone: string;
  address: string;
  onu_external_id: string;
  onu_sn?: string | null;
  onu_mac?: string | null;
  olt_id?: string | null;
  board?: string | null;
  port?: string | null;
  onu_number?: string | null;
  onu_status?: string | null;
  status: "suspended_unpaid" | "manually_exempted" | "temporary_reactivated" | "external_override_detected" | "resolved_paid" | string;
  amount_overdue: number;
  due_date?: string | null;
  invoice_id?: string | null;
  invoice_number?: string | null;
  suspended_at?: string | null;
  grace_period_expired_at?: string | null;
  exempted_at?: string | null;
  exempted_by_id?: string | null;
  exempted_by_name?: string | null;
  exemption_reason?: string | null;
  temporary_reactivation_until?: string | null;
  temporary_reactivation_days?: number | null;
  temporary_reactivation_reason?: string | null;
  reactivated_at?: string | null;
  reactivated_by_id?: string | null;
  last_smartolt_admin_status?: string | null;
  last_smartolt_status_checked_at?: string | null;
  external_override_detected_at?: string | null;
  external_override_note?: string | null;
  last_enforcement_action_at?: string | null;
  resolved_at?: string | null;
  created_at?: string | null;
};

export type SmartOLTBillingSuspensionStats = {
  currently_suspended: number;
  manually_exempted: number;
  temporary_reactivated?: number;
  external_overrides?: number;
  resolved_today: number;
  total_overdue_amount: number;
};
